import React from 'react';
import { useLocation } from 'react-router-dom';
import { Search, User, HelpCircle, Menu } from 'lucide-react';
import { useAuthStore, useProjectStore } from '../../store';
import { useHelpStore } from '../../store/helpStore';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import { NotificationBell } from '../notifications';
import Tooltip from '../common/Tooltip';
import { ASSISTANT_NAME } from '../../constants/branding';

/**
 * Derive a page title from the current route path
 */
const usePageTitle = (): { title: string; subtitle?: string } => {
  const location = useLocation();
  const { currentProject } = useProjectStore();
  const segments = location.pathname.split('/').filter(Boolean);

  // Top-level pages
  const topLevelTitles: Record<string, string> = {
    dashboard: 'Command Center',
    projects: 'Projects',
    assistant: ASSISTANT_NAME,
    bootcamp: 'Bootcamp',
    'knowledge-base': 'Knowledge Base',
    'whats-new': "What's New",
    settings: 'Settings',
    login: 'Login',
    register: 'Register',
  };

  // Project sub-pages
  const projectPageTitles: Record<string, string> = {
    agents: 'Agents',
    schemas: 'Schemas',
    workflows: 'Workflows',

    registries: 'Registries',
    queues: 'Queues',
    history: 'History',
  };

  // /projects/:id/workflows etc.
  if (segments[0] === 'projects' && segments.length >= 3) {
    const subPage = segments[2];
    const pageTitle = projectPageTitles[subPage] || subPage.charAt(0).toUpperCase() + subPage.slice(1);
    return {
      title: pageTitle,
      subtitle: currentProject?.name,
    };
  }

  // /projects/:id (project dashboard)
  if (segments[0] === 'projects' && segments.length === 2) {
    return {
      title: 'Overview',
      subtitle: currentProject?.name,
    };
  }

  // Bootcamp sub-pages
  const bootcampPageTitles: Record<string, string> = {
    documentation: 'Documentation',
    tutorials: 'Tutorials',
    assistant: ASSISTANT_NAME,
  };

  if (segments[0] === 'bootcamp' && segments.length >= 2) {
    return {
      title: bootcampPageTitles[segments[1]] || segments[1].charAt(0).toUpperCase() + segments[1].slice(1),
      subtitle: 'Bootcamp',
    };
  }

  // Top-level pages
  const firstSegment = segments[0] || 'dashboard';
  return {
    title: topLevelTitles[firstSegment] || firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1),
  };
};

interface HeaderProps {
  onMenuToggle?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const { user } = useAuthStore();
  const { toggleHelp } = useHelpStore();
  const { navigateWithContext } = useContextualNavigation();
  const { title, subtitle } = usePageTitle();

  return (
    <header className="relative z-10 bg-white dark:bg-cap-dark border-b border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 sm:py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Hamburger + Page context */}
        <div className="flex items-center space-x-3">
          {onMenuToggle && (
            <button
              onClick={onMenuToggle}
              className="p-2.5 mr-2 text-gray-600 dark:text-gray-400 md:hidden min-w-[48px] min-h-[48px] flex items-center justify-center"
              aria-label="Open navigation menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-warm-white leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Right side - Search and user menu */}
        <div className="flex items-center space-x-4">
          <Tooltip content="Search" position="bottom">
            <button
              className="hidden sm:block p-2 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Search className="w-5 h-5" />
            </button>
          </Tooltip>
          <Tooltip content="Help" position="bottom">
            <button
              onClick={toggleHelp}
              aria-label="Help"
              className="hidden sm:block p-2 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </Tooltip>
          <NotificationBell />
          <Tooltip content="View your profile">
          <button
            onClick={() => {
              const userId = (user as any)?.user_id ?? user?.id;
              if (userId) {
                navigateWithContext(`/users/${userId}`, subtitle ? `${title} - ${subtitle}` : title);
              }
            }}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center dark:text-gray-400">
              <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </div>
            <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300">{user?.username}</span>
          </button>
          </Tooltip>
        </div>
      </div>
    </header>
  );
};

export default Header;
